import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Invoice, InvoiceItem, DebitNote } from '../../types';
import { useCreateDebitNote } from '../../hooks/useDebitNotes';
import { useCurrencies } from '../../hooks/useCurrencies';
import { useFOAs } from '../../hooks/useFOAs';
import { useClients } from '../../hooks/useClients';
import { useBranches } from '../../hooks/useBranches';
import { useBanks } from '../../hooks/useBanks';
import TextArea from './TextArea';
import Button from './Button';
import { formatDateForAPI, formatDateForInput } from '../../utils/dateFormatter';
import InvoiceFormFields from './InvoiceFormFields';
import InvoiceItemsTable from './InvoiceItemsTable';
import InvoiceFinancialSection from './InvoiceFinancialSection';
import InvoiceAmountReceivedSection, { AmountReceivedRow } from './InvoiceAmountReceivedSection';
import InvoiceDebitNotesSection from './InvoiceDebitNotesSection';

interface InvoiceFormProps {
  invoice?: Invoice;
  onSubmit: (data: Partial<Invoice>) => void;
  onCancel: () => void;
}

const calcDays = (from: string, to: string): number => {
  if (!from || !to) return 1;
  const diffTime = new Date(to).getTime() - new Date(from).getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays > 0 ? diffDays : 1;
};

const InvoiceForm: React.FC<InvoiceFormProps> = ({ invoice, onSubmit, onCancel }) => {
  const [debitNotes, setDebitNotes] = useState<DebitNote[]>(() => {
    if (invoice && Array.isArray(invoice.debitNotes) && invoice.debitNotes.length > 0) {
      if (typeof invoice.debitNotes[0] === 'object') return invoice.debitNotes as DebitNote[];
    }
    return [];
  });
  const [newDebitNote, setNewDebitNote] = useState<Partial<DebitNote>>({});
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>(() => {
    if (invoice?.items && invoice.items.length > 0) {
      return invoice.items.map(item => ({
        ...item,
        amount: item.amount ?? item.unitPrice ?? 0,
        fromDate: item.fromDate || '',
        toDate: item.toDate || '',
        currency: item.currency || invoice.currency || '',
      }));
    }
    return [];
  });
  const [amountReceivedRows, setAmountReceivedRows] = useState<AmountReceivedRow[]>([]);

  const createDebitNoteMutation = useCreateDebitNote();
  const { data: currencies = [] } = useCurrencies({ populate: '*' });
  const foaQueryOptions = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: foas = [] } = useFOAs(foaQueryOptions);
  const clientQueryOptions = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: clients = [] } = useClients(clientQueryOptions);
  const branchQueryOptions = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: branches = [] } = useBranches(branchQueryOptions);
  const bankQueryOptions = useMemo(() => ({ pagination: { pageSize: 200 } }), []);
  const { data: banks = [] } = useBanks(bankQueryOptions);

  const { register, handleSubmit, formState: { errors, isSubmitting }, control, watch, setValue } = useForm<Invoice>({
    defaultValues: invoice ? {
      ...invoice, invoiceType: invoice.invoiceType || 'standard', freeTextContent: invoice.freeTextContent || '',
      issueDate: formatDateForInput(invoice.issueDate), operationDate: formatDateForInput(invoice.operationDate),
      dueDate: formatDateForInput(invoice.dueDate),
      currency: (typeof invoice.currency === 'object' && invoice.currency !== null
        ? ((invoice.currency as any).documentId || String((invoice.currency as any).id))
        : invoice.currency) as any
    } : {
      invoiceType: 'standard', clientId: '', vesselId: '', foaId: '', freeTextContent: '', items: [],
      subtotal: 0, tax: 0, deduction: 0, netAfterTaxAndDeduction: 0, total: 0,
      performanceBond: 0, paid: 0, netRemaining: 0, currency: undefined, exchangeRate: undefined,
      status: 'draft', issueDate: '', operationDate: '', dueDate: '', description: '', notes: ''
    }
  });

  const selectedCurrency = useWatch({ control, name: 'currency' });
  const invoiceType = useWatch({ control, name: 'invoiceType' }) || 'standard';
  const selectedFoaId = useWatch({ control, name: 'foaId' });
  const subtotal = watch('subtotal');
  const deduction = watch('deduction');
  const paid = watch('paid');
  const isDebitNote = invoiceType === 'debit_note';

  // Currency is a Strapi relation - option values must be documentIds, not codes
  const currencyOptions = useMemo(() => {
    if (!currencies || currencies.length === 0) return [{ value: '', label: 'Select Currency' }];
    return [
      { value: '', label: 'Select Currency' },
      ...currencies.filter((c: any) => c.isActive !== false)
        .map((c: any) => ({ value: c.documentId || String(c.id), label: `${c.code || ''} (${c.name || c.code || 'Unknown'})` }))
    ];
  }, [currencies]);

  const selectedCurrencyCode = useMemo(() => {
    const c: any = (currencies as any[]).find(
      (cur: any) => (cur.documentId || String(cur.id)) === selectedCurrency || cur.code === selectedCurrency
    );
    return c?.code as string | undefined;
  }, [currencies, selectedCurrency]);

  const bankOptions = useMemo(() => (banks as any[]).map((b: any) => ({
    value: b.name || b.documentId || '',
    label: `${b.name || ''}${b.accountNumber ? ` (${b.accountNumber})` : ''}`,
  })), [banks]);

  const clientOptions = useMemo(() => [
    { value: '', label: 'Select Client' },
    ...clients.map((c: any) => ({ value: c.documentId || c.id, label: c.name || '' }))
  ], [clients]);

  const branchOptions = useMemo(() => [
    { value: '', label: 'Select Vessel/HQ' },
    ...branches.map((b: any) => ({ value: b.documentId || b.id, label: b.name || '' }))
  ], [branches]);

  const foaOptions = useMemo(() => [
    { value: '', label: 'Select FOA (Optional)' },
    ...foas.map((f: any) => ({ value: f.documentId || f.id, label: `${f.foaNumber || ''} - ${f.englishName || ''}` }))
  ], [foas]);

  const statusOptions = [
    { value: 'draft', label: 'Draft' }, { value: 'sent', label: 'Sent' },
    { value: 'paid', label: 'Paid' }, { value: 'overdue', label: 'Overdue' },
    { value: 'partially_paid', label: 'Partially Paid' }
  ];

  const invoiceTypeOptions = [
    { value: 'standard', label: 'Standard Invoice' },
    { value: 'debit_note', label: 'Debit Note' }
  ];

  useEffect(() => {
    if (selectedFoaId && foas.length > 0) {
      const selectedFoa = foas.find((f: any) => (f.documentId || f.id) === selectedFoaId);
      if (selectedFoa) setValue('subtotal', selectedFoa.amount1 ?? selectedFoa.amount ?? 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFoaId, foas]);

  useEffect(() => { if (!isDebitNote) setValue('tax', subtotal * 0.03); }, [subtotal, setValue, isDebitNote]);
  useEffect(() => {
    setValue('netAfterTaxAndDeduction', isDebitNote ? subtotal - deduction : subtotal - subtotal * 0.03 - deduction);
  }, [subtotal, deduction, setValue, isDebitNote]);
  useEffect(() => { setValue('total', isDebitNote ? subtotal : subtotal + subtotal * 0.03); }, [subtotal, setValue, isDebitNote]);
  useEffect(() => {
    setValue('netRemaining', (isDebitNote ? subtotal : subtotal + subtotal * 0.03) - paid);
  }, [subtotal, paid, setValue, isDebitNote]);

  const recalcSubtotal = useCallback((items: InvoiceItem[]) => {
    setValue('subtotal', items.reduce((sum: number, item: InvoiceItem) => sum + (item.total || 0), 0));
  }, [setValue]);

  const addInvoiceItem = () => {
    setInvoiceItems(prev => [...prev, {
      id: Date.now().toString(), description: '', fromDate: '', toDate: '',
      quantity: 1, amount: 0, currency: (selectedCurrency as string) || '', total: 0,
    }]);
  };

  const removeInvoiceItem = (id: string) => {
    const updated = invoiceItems.filter(item => item.id !== id);
    setInvoiceItems(updated);
    recalcSubtotal(updated);
  };

  const updateInvoiceItem = (id: string, field: keyof InvoiceItem, value: any) => {
    const updated = invoiceItems.map(item => {
      if (item.id !== id) return item;
      const u = { ...item, [field]: value };
      if (field === 'fromDate' || field === 'toDate') {
        const from = field === 'fromDate' ? value : u.fromDate;
        const to = field === 'toDate' ? value : u.toDate;
        if (from && to) { u.quantity = calcDays(from, to); u.total = u.quantity * u.amount; }
      }
      if (field === 'quantity' || field === 'amount') {
        u.total = (field === 'quantity' ? value : u.quantity) * (field === 'amount' ? value : u.amount);
      }
      return u;
    });
    setInvoiceItems(updated);
    recalcSubtotal(updated);
  };

  const addAmountReceivedRow = () => {
    setAmountReceivedRows(prev => [...prev, {
      id: Date.now().toString(), date: '', chequeNo: '', fromBank: '', toBank: '',
      vessel: '', description: '', invoiceRef: '', paidUSD: 0, paidEGP: 0, actualUSD: 0, actualRate: 0, actualEGP: 0,
    }]);
  };

  const updateAmountReceivedRow = (id: string, field: string, value: any) => {
    setAmountReceivedRows(prev => prev.map(row => {
      if (row.id !== id) return row;
      const u = { ...row, [field]: value };
      if (field === 'actualUSD' || field === 'actualRate') {
        u.actualEGP = (field === 'actualUSD' ? value : u.actualUSD) * (field === 'actualRate' ? value : u.actualRate);
      }
      return u;
    }));
  };

  const removeAmountReceivedRow = (id: string) => {
    setAmountReceivedRows(prev => prev.filter(row => row.id !== id));
  };

  const handleFormSubmit = async (data: Invoice) => {
    try {
      const debitNoteDocumentIds: string[] = [];
      for (const dn of debitNotes) {
        if (!dn.documentId) {
          const created = await createDebitNoteMutation.execute({
            invoiceNumber: dn.invoiceNumber, date: formatDateForAPI(dn.date),
            vessel: dn.vessel || 'N/A', description: dn.description,
            amount: dn.amount, deduction: dn.deduction, paid: dn.paid,
            netRemaining: dn.netRemaining, status: dn.debit_note_status || 'pending',
          });
          if (created?.documentId) debitNoteDocumentIds.push(created.documentId);
        } else {
          debitNoteDocumentIds.push(dn.documentId);
        }
      }
      const submitData: Partial<Invoice> = {
        ...data, invoiceType: data.invoiceType || 'standard',
        freeTextContent: isDebitNote ? data.freeTextContent : undefined,
        issueDate: formatDateForAPI(data.issueDate), operationDate: formatDateForAPI(data.operationDate),
        dueDate: formatDateForAPI(data.dueDate),
        exchangeRate: !isDebitNote && selectedCurrencyCode === 'USD' ? data.exchangeRate : undefined,
        debitNotes: debitNoteDocumentIds.length > 0 ? debitNoteDocumentIds : undefined,
        items: invoiceItems.length > 0 ? invoiceItems : [{
          id: '1', description: data.description || 'Service', quantity: 1, amount: data.subtotal, total: data.subtotal
        }],
      };
      onSubmit({ ...submitData, ...(invoice ? { id: invoice.id, documentId: invoice.documentId } : {}) });
    } catch (error) {
      console.error('Error during invoice submission:', error);
      alert('Failed to create invoice. Please try again.');
    }
  };

  const addDebitNote = () => {
    if (newDebitNote.invoiceNumber && newDebitNote.description) {
      const dn: DebitNote = {
        id: Date.now().toString(), invoiceId: invoice?.id || '',
        invoiceNumber: newDebitNote.invoiceNumber || '',
        date: newDebitNote.date || new Date().toISOString().split('T')[0],
        vessel: newDebitNote.vessel || 'N/A', description: newDebitNote.description || '',
        amount: { usd: newDebitNote.amount?.usd || 0, egp: newDebitNote.amount?.egp || 0 },
        deduction: { usd: newDebitNote.deduction?.usd || 0, egp: newDebitNote.deduction?.egp || 0 },
        paid: { usd: newDebitNote.paid?.usd || 0, egp: newDebitNote.paid?.egp || 0 },
        netRemaining: { usd: newDebitNote.netRemaining?.usd || 0, egp: newDebitNote.netRemaining?.egp || 0 },
        debit_note_status: 'pending', createdAt: new Date().toISOString()
      };
      setDebitNotes([...debitNotes, dn]);
      setNewDebitNote({});
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {invoice ? 'Edit Invoice' : 'Add New Invoice'}
      </h2>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <InvoiceFormFields
          register={register} errors={errors} isDebitNote={isDebitNote}
          invoiceTypeOptions={invoiceTypeOptions} clientOptions={clientOptions}
          statusOptions={statusOptions} branchOptions={branchOptions} foaOptions={foaOptions}
          selectedFoaId={selectedFoaId as string | undefined} foas={foas}
        />
        {!isDebitNote && (
          <InvoiceItemsTable invoiceItems={invoiceItems} currencyOptions={currencyOptions}
            onAddItem={addInvoiceItem} onRemoveItem={removeInvoiceItem} onUpdateItem={updateInvoiceItem} />
        )}
        <InvoiceFinancialSection register={register} errors={errors} isDebitNote={isDebitNote}
          selectedCurrency={selectedCurrencyCode} currencyOptions={currencyOptions} />
        <InvoiceAmountReceivedSection rows={amountReceivedRows} onAddRow={addAmountReceivedRow}
          onUpdateRow={updateAmountReceivedRow} onRemoveRow={removeAmountReceivedRow} bankOptions={bankOptions} />
        <InvoiceDebitNotesSection debitNotes={debitNotes} newDebitNote={newDebitNote}
          onNewDebitNoteChange={setNewDebitNote} onAddDebitNote={addDebitNote} />
        <TextArea label="Notes" {...register('notes')} error={errors.notes?.message} placeholder="Enter any additional notes" rows={3} />
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {invoice ? 'Update Invoice' : 'Create Invoice'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;
